import { Component, computed, inject, input, output, Signal } from '@angular/core';
import { ClipboardBarcode, PaceBarcode } from './pace-barcode-clipboard.model';
import { DatePipe } from '@angular/common';
import { faArrowLeft, faHandPointer, faQrcode, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ScanUtilities } from '../utils/scan-utilities/scan-utilities.service';
import { ApplicationIdentifier } from '../utils/scan-utilities/scan.enum';
import { matchesPattern } from '../utils/scan-utilities/scan.utils';
import { prepareZXingModule, writeBarcode, WriterOptions } from 'zxing-wasm/full';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TooltipDirective } from 'ngx-bootstrap/tooltip';
import { LogInstanceToken } from '@evorait/pace-logging';

prepareZXingModule({
    overrides: {
        locateFile: (path: string, prefix: string) => {
            if (path.endsWith('.wasm')) {
                return `${path}`;
            }
            return prefix + path;
        },
    },
});

@Component({
    selector: 'pace-barcode-clipboard',
    imports: [DatePipe, FontAwesomeModule, TooltipDirective],
    templateUrl: './pace-barcode-clipboard.component.html',
    styleUrl: './pace-barcode-clipboard.component.scss',
    standalone: true,
})
export class PaceBarcodeClipboardComponent {
    private readonly scanUtilities = inject(ScanUtilities);
    private readonly sanitizer = inject(DomSanitizer);
    private readonly log = inject(LogInstanceToken);

    public readonly barcodes = input.required<PaceBarcode[]>();
    public readonly pattern = input<string | undefined>(undefined);
    public readonly appId = input<ApplicationIdentifier>(ApplicationIdentifier.Default);
    public readonly pickableBarcodes = input<boolean>(false);
    public readonly deletableBarcodes = input<boolean>(true);
    public readonly qrableBarcodes = input<boolean>(true);

    public readonly deleteTitle = input<string>('Delete');
    public readonly pickTitle = input<string>('Select');
    public readonly qrTitle = input<string>('Show QR Code');
    public readonly qrBackLabel = input<string>('Back');
    public readonly mismatchTooltip = input<string>('Barcode does not match required pattern.');

    public identifiedBarcodes: Signal<ClipboardBarcode[]> = computed(() => {
        const pattern = this.pattern();
        const appId = this.appId();
        return this.barcodes().map((barcode) => {
            return {
                ...barcode,
                matchesPattern: matchesPattern(barcode.barcode, pattern),
                barcodeDisplayString: this.scanUtilities.getBarcodeDisplayString(barcode.barcode, appId, pattern),
            };
        });
    });

    public readonly barcodeUpdates = output<PaceBarcode[]>();
    public readonly barcodePick = output<PaceBarcode>();

    public readonly deleteIcon = faTrash;
    public readonly pickIcon = faHandPointer;
    public readonly qrIcon = faQrcode;
    public readonly qrBackIcon = faArrowLeft;

    public svg: SafeHtml | undefined;

    public deleteBarcode(barcodeId: string): void {
        this.barcodeUpdates.emit(this.barcodes().filter((b) => b.id !== barcodeId));
    }

    public pickBarcode(barcode: PaceBarcode): void {
        this.barcodePick.emit(barcode);
    }

    public qrizeBarcode(barcode: PaceBarcode): void {
        const writerOptions: WriterOptions = {
            format: 'QRCode',
            scale: 10,
            withHRT: true,
            withQuietZones: false,
        };
        writeBarcode(barcode.barcode, writerOptions)
            .then((result) => {
                let svg = result.svg;
                const rect = svg.match(/rect x="(\d+)" y="(\d+)" width="(\d+)" height="(\d+)"/);
                if (rect?.length === 5) {
                    // remove the preset width/height and add proper viewbox to allow for scaling
                    svg = svg.replace(/(<svg .*)width="[^"]+"(.*)/, `$1$2`);
                    svg = svg.replace(/(<svg .*)height="[^"]+"(.*)/, `$1$2`);
                    svg = svg.replace(
                        /<svg/,
                        `<svg viewBox="${rect[1]} ${rect[2]} ${rect[3]} ${rect[4]}" style="max-height: calc(90vh - 80px);"`,
                    );
                }
                this.svg = this.sanitizer.bypassSecurityTrustHtml(svg);
            })
            .catch((e) => this.log.error(`${this.constructor.name}: error creating barcode: ${JSON.stringify(e)}`));
    }

    public removeQr(): void {
        this.svg = undefined;
    }
}
